import React, { forwardRef, memo } from 'react'
import { designTokens } from '@/utils/design-tokens'

// Updated with semantic colors support

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  size?: 'xsmall' | 'small' | 'medium' | 'large'
  color?: 'white' | 'dark' | 'bare' | 'light' | 'success' | 'danger' | 'warning'
  state?: 'default' | 'focus' | 'disable'
  children?: React.ReactNode
}

const Button = memo(forwardRef<HTMLButtonElement, ButtonProps>(
  ({ 
    size = 'medium', 
    color = 'white',
    state = 'default',
    disabled,
    className = '',
    children,
    onMouseEnter,
    onMouseLeave,
    onFocus,
    onBlur,
    style,
    ...props
  }, ref) => {
    const [isFocused, setIsFocused] = React.useState(false)
    const [_isHovered, setIsHovered] = React.useState(false)

    // Determine actual state
    const actualState = disabled ? 'disable' : 
                       isFocused ? 'focus' : 
                       state

    const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
      setIsHovered(true)
      onMouseEnter?.(e)
    }

    const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
      setIsHovered(false)
      onMouseLeave?.(e)
    }

    const handleFocus = (e: React.FocusEvent<HTMLButtonElement>) => {
      setIsFocused(true)
      onFocus?.(e)
    }

    const handleBlur = (e: React.FocusEvent<HTMLButtonElement>) => {
      setIsFocused(false)
      onBlur?.(e)
    }

    const sizeClasses = {
      xsmall: 'h-7 px-2 text-xs gap-1 font-semibold',
      small: 'h-[34px] px-2.5 text-sm gap-1 font-semibold',
      medium: 'h-10 px-2.5 text-base gap-1 font-semibold',
      large: 'h-12 px-2.5 text-lg gap-1 font-semibold'
    }

    const getColorStateStyles = (color: string, state: string) => {
      const colorStyles = {
        white: {
          default: `border-2 hover:opacity-90`,
          focus: `border-2`,
          disable: `border-2 cursor-not-allowed opacity-50`
        },
        dark: {
          default: `border-2 hover:opacity-90`,
          focus: `border-2`,
          disable: `border-2 cursor-not-allowed opacity-50`
        },
        bare: {
          default: `border-0 hover:opacity-90`,
          focus: `border-0`,
          disable: `border-0 cursor-not-allowed opacity-50`
        },
        light: {
          default: `border-2 hover:opacity-90`,
          focus: `border-2`,
          disable: `border-2 cursor-not-allowed opacity-50`
        },
        success: {
          default: `border-2 hover:opacity-90`,
          focus: `border-2`,
          disable: `border-2 cursor-not-allowed opacity-50`
        },
        danger: {
          default: `border-2 hover:opacity-90`,
          focus: `border-2`,
          disable: `border-2 cursor-not-allowed opacity-50`
        },
        warning: {
          default: `border-2 hover:opacity-90`,
          focus: `border-2`,
          disable: `border-2 cursor-not-allowed opacity-50`
        }
      }

      const colorMap = {
        white: {
          default: { 
            backgroundColor: designTokens.colors.white, 
            borderColor: designTokens.colors.gray[1], 
            color: designTokens.colors.gray[6] 
          },
          focus: { 
            backgroundColor: designTokens.colors.white, 
            borderColor: designTokens.colors.secondary.dark, 
            color: designTokens.colors.gray[6] 
          },
          disable: { 
            backgroundColor: designTokens.colors.gray[0], 
            borderColor: designTokens.colors.gray[1], 
            color: designTokens.colors.gray[3] 
          }
        },
        dark: {
          default: { 
            backgroundColor: designTokens.colors.primary.dark, 
            borderColor: designTokens.colors.primary.dark, 
            color: designTokens.colors.white 
          },
          focus: { 
            backgroundColor: designTokens.colors.primary.dark, 
            borderColor: designTokens.colors.primary.light, 
            color: designTokens.colors.white 
          },
          disable: { 
            backgroundColor: designTokens.colors.primary.light, 
            borderColor: designTokens.colors.primary.light, 
            color: designTokens.colors.white 
          }
        },
        bare: {
          default: { 
            backgroundColor: 'transparent', 
            borderColor: 'transparent', 
            color: designTokens.colors.gray[6] 
          },
          focus: { 
            backgroundColor: 'transparent', 
            borderColor: 'transparent', 
            color: designTokens.colors.info 
          },
          disable: { 
            backgroundColor: 'transparent', 
            borderColor: 'transparent', 
            color: designTokens.colors.gray[3] 
          }
        },
        light: {
          default: {
            backgroundColor: designTokens.colors.gray[1],
            borderColor: designTokens.colors.gray[1],
            color: designTokens.colors.gray[6]
          },
          focus: {
            backgroundColor: designTokens.colors.gray[2],
            borderColor: designTokens.colors.gray[2],
            color: designTokens.colors.gray[6]
          },
          disable: {
            backgroundColor: designTokens.colors.gray[0],
            borderColor: designTokens.colors.gray[1],
            color: designTokens.colors.gray[3]
          }
        },
        success: {
          default: {
            backgroundColor: designTokens.colors.success.DEFAULT,
            borderColor: designTokens.colors.success.DEFAULT,
            color: designTokens.colors.white
          },
          focus: {
            backgroundColor: designTokens.colors.success.dark,
            borderColor: designTokens.colors.success.dark,
            color: designTokens.colors.white
          },
          disable: {
            backgroundColor: designTokens.colors.gray[0],
            borderColor: designTokens.colors.gray[1],
            color: designTokens.colors.gray[3]
          }
        },
        danger: {
          default: {
            backgroundColor: designTokens.colors.error.DEFAULT,
            borderColor: designTokens.colors.error.DEFAULT,
            color: designTokens.colors.white
          },
          focus: {
            backgroundColor: designTokens.colors.error.dark,
            borderColor: designTokens.colors.error.dark,
            color: designTokens.colors.white
          },
          disable: {
            backgroundColor: designTokens.colors.gray[0],
            borderColor: designTokens.colors.gray[1],
            color: designTokens.colors.gray[3]
          }
        },
        warning: {
          default: {
            backgroundColor: designTokens.colors.warning.DEFAULT,
            borderColor: designTokens.colors.warning.DEFAULT,
            color: designTokens.colors.white
          },
          focus: {
            backgroundColor: designTokens.colors.warning.dark,
            borderColor: designTokens.colors.warning.dark,
            color: designTokens.colors.white
          },
          disable: {
            backgroundColor: designTokens.colors.gray[0],
            borderColor: designTokens.colors.gray[1],
            color: designTokens.colors.gray[3]
          }
        }
      }

      return {
        className: colorStyles[color as keyof typeof colorStyles][state as keyof typeof colorStyles.white],
        style: colorMap[color as keyof typeof colorMap][state as keyof typeof colorMap.white]
      }
    }

    const { className: colorClassName, style: colorStyle } = getColorStateStyles(color, actualState)

    return (
      <button
        ref={ref}
        disabled={disabled}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={`
          inline-flex items-center justify-center rounded
          transition-all duration-150
          ${sizeClasses[size]}
          ${colorClassName}
          ${className}
        `}
        {...props}
        style={{
          fontFamily: designTokens.typography.fontFamily.sans[0],
          ...colorStyle,
          ...style
        }}
      >
        {children}
      </button>
    )
  }
))

Button.displayName = 'Button'

export default Button